import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { nombreSeguroStorage } from './lib/nombre-storage';
import { PLAN_LABELS, PLAN_COLORS } from './CompanyContext';
import type { LayoutRequest } from './LayoutPro';
import { STATUS_CFG, LayoutIcon } from './LayoutPro';
import { PLAN_LIMITS as CENTRAL_PLAN_LIMITS, PlanId, MONTHLY_PRICES, ANNUAL_MONTHLY_PRICES } from './lib/pricing';
import { passesModulo11 } from './lib/validateRuc';
import { validateFileSize } from './lib/validateFileSize';
import EnlaceArchivo from './components/EnlaceArchivo';
import { aFechaLocal, diaEc, fechaCortaLocal, finDiaEc, hoyEc, inicioDiaEc, sumarDiasDia } from './lib/fechas';
import './SuperAdmin.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const ACTIVITY_MODULE_LABELS: Record<string, string> = {
  product:    'Productos',
  warehouse:  'Bodegas',
  zone:       'Zonas',
  user:       'Usuarios',
  invitation: 'Invitaciones',
  company:    'Empresa',
};

// Precios derivados de lib/pricing.ts — única fuente de verdad para evitar que
// el MRR estimado y los formularios diverjan del pricing real (bug histórico:
// el descuento anual se calculaba con un 0.8 uniforme en vez de los precios
// anuales reales, lo cual en el caso de Enterprise arrastraba redondeos).
function planPrice(plan: string, cycle: string): number {
  const p = plan as PlanId;
  if (cycle === 'annual') return ANNUAL_MONTHLY_PRICES[p] ?? 0;
  return MONTHLY_PRICES[p] ?? 0;
}

// Deriva del mapeo central en lib/pricing.ts — única fuente de verdad para los
// límites por plan (evita que la provisión de empresas y PricingPage diverjan).
const PLAN_LIMITS: Record<string, { max_warehouses: number | null; max_products: number | null; max_users: number | null }> =
  (['starter', 'professional', 'enterprise'] as PlanId[]).reduce((acc, plan) => {
    acc[plan] = {
      max_warehouses: CENTRAL_PLAN_LIMITS[plan].warehouses,
      max_products:   CENTRAL_PLAN_LIMITS[plan].products,
      max_users:      CENTRAL_PLAN_LIMITS[plan].users,
    };
    return acc;
  }, {} as Record<string, { max_warehouses: number | null; max_products: number | null; max_users: number | null }>);

const COUNTRIES = [
  { code: 'EC', name: 'Ecuador'   },
  { code: 'CO', name: 'Colombia'  },
  { code: 'PE', name: 'Perú'      },
  { code: 'CL', name: 'Chile'     },
  { code: 'AR', name: 'Argentina' },
  { code: 'MX', name: 'México'    },
  { code: 'US', name: 'EE. UU.'   },
  { code: 'OTHER', name: 'Otro'   },
];

// ── RUC validation (Ecuador) ──────────────────────────────────────────────────

function validateRucEdit(ruc: string): string | null {
  const digits = ruc.replace(/\D/g, '');
  if (digits.length !== 13) return 'El RUC debe tener exactamente 13 dígitos numéricos.';
  if (!digits.endsWith('001')) return 'El RUC debe terminar en 001.';
  if (!passesModulo11(digits)) return 'El RUC no es válido (no pasa la validación módulo 11).';
  return null;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface TrialExtensionRequest {
  id:           string;
  company_id:   string;
  requested_at: string;
  status:       'pending' | 'approved' | 'rejected';
  reviewed_at:  string | null;
  user_message: string | null;
  companies: {
    name:     string;
    ruc:      string | null;
    profiles: { job_title: string | null; full_name: string | null }[];
  } | null;
}

export interface CompanyWithStats {
  id:               string;
  name:             string;
  ruc:              string | null;
  country:          string | null;
  plan:             'starter' | 'professional' | 'enterprise';
  billing_cycle:    'monthly' | 'annual';
  active:           boolean;
  created_at:       string;
  trial_ends_at:    string | null;
  next_billing_at:  string | null;
  max_warehouses:   number | null;
  max_products:     number | null;
  max_users:        number | null;
  user_count:               number;
  product_count:            number;
  warehouse_count:          number;
  worker_count:             number;
  emergency_access_enabled: boolean | null;
  corporate_domain:         string | null;
  selected_plan:            string | null;
  payment_status:           'trial' | 'active' | 'overdue' | 'cancelled' | null;
  last_payment_date:        string | null;
  next_payment_date:        string | null;
  subscription_status:      'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled' | null;
  current_period_end:       string | null;
  admin_last_sign_in_at:    string | null;
  direccion:                string | null;
  is_dev:                   boolean;
}

interface Payment {
  id:           string;
  company_id:   string;
  amount:       number;
  payment_date: string;
  method:       'transferencia' | 'efectivo' | 'otro';
  reference:    string | null;
  notes:        string | null;
  created_at:   string;
}

interface PaymentForm {
  amount:       string;
  payment_date: string;
  method:       'transferencia' | 'efectivo' | 'otro';
  reference:    string;
  notes:        string;
}

interface LayoutRequestAdmin extends LayoutRequest {
  companies: { name: string } | null;
}

interface NewCompanyForm {
  name:          string;
  ruc:           string;
  country:       string;
  plan:          'starter' | 'professional' | 'enterprise';
  billing_cycle: 'monthly' | 'annual';
  trial_days:    number;
}

type CompanyStatus = 'trial' | 'active' | 'overdue' | 'inactive';

interface PromoCode {
  id:                  string;
  code:                string;
  discount_percentage: number;
  description:         string | null;
  active:              boolean;
  uses_count:          number;
  max_uses:            number | null;
  created_at:          string;
  expires_at:          string | null;
}

interface PromoCodeForm {
  code:                string;
  discount_percentage: string;
  description:         string;
  max_uses:            string;
  expires_at:          string;
}

const DEFAULT_FORM: NewCompanyForm = {
  name:          '',
  ruc:           '',
  country:       'EC',
  plan:          'starter',
  billing_cycle: 'monthly',
  trial_days:    14,
};

const DEFAULT_PAYMENT: PaymentForm = {
  amount:       '',
  payment_date: hoyEc(),
  method:       'transferencia',
  reference:    '',
  notes:        '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getStatus(c: CompanyWithStats): CompanyStatus {
  if (!c.active) return 'inactive';
  if (c.payment_status === 'overdue')    return 'overdue';
  if (c.payment_status === 'active')     return 'active';
  if (c.payment_status === 'cancelled')  return 'inactive';
  if (c.trial_ends_at && new Date(c.trial_ends_at) > new Date()) return 'trial';
  return 'active';
}

function computeMRR(companies: CompanyWithStats[]): number {
  return companies
    .filter((c) => c.active)
    .reduce((acc, c) => acc + planPrice(c.plan, c.billing_cycle), 0);
}

// Fecha sin hora, tal cual; un instante (next_billing_at, trial_ends_at...), en el día de Ecuador (lib/fechas).
function fmtDate(d: string | null): string {
  return fechaCortaLocal(d, 'es-CL', { year: 'numeric', month: 'short', day: 'numeric' });
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 86_400_000);
}

// Días entre ahora y una fecha futura (negativo si ya pasó).
function daysUntil(d: string | null): number | null {
  if (!d) return null;
  return Math.floor((new Date(d).getTime() - Date.now()) / 86_400_000);
}

// Días transcurridos desde una fecha pasada.
function daysSince(d: string | null): number | null {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000);
}

interface InvitationLog {
  id: string;
  email: string;
  role: string;
  company_name: string;
  invited_by_email: string;
  created_at: string;
  accepted_at: string | null;
  expires_at: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface SuperAdminProps {
  onEnterCompany?: (company: CompanyWithStats) => void;
}

export default function SuperAdmin({ onEnterCompany }: SuperAdminProps) {
  const [companies,  setCompanies]  = useState<CompanyWithStats[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Inline edit company name
  const [editingNameId,    setEditingNameId]    = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [savingNameId,     setSavingNameId]     = useState<string | null>(null);

  // Inline edit company RUC
  const [editingRucId,    setEditingRucId]    = useState<string | null>(null);
  const [editingRucValue, setEditingRucValue] = useState('');
  const [savingRucId,     setSavingRucId]     = useState<string | null>(null);

  // New company modal
  const [newOpen,    setNewOpen]    = useState(false);
  const [newForm,    setNewForm]    = useState<NewCompanyForm>(DEFAULT_FORM);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState('');

  // Edit plan modal
  const [editCompany,     setEditCompany]     = useState<CompanyWithStats | null>(null);
  const [editPlan,        setEditPlan]        = useState<'starter' | 'professional' | 'enterprise'>('starter');
  const [editCycle,       setEditCycle]       = useState<'monthly' | 'annual'>('monthly');
  const [editNextBilling, setEditNextBilling] = useState('');
  const [editSaving,      setEditSaving]      = useState(false);

  // Payment modal
  const [paymentCompany, setPaymentCompany] = useState<CompanyWithStats | null>(null);
  const [paymentForm,    setPaymentForm]    = useState<PaymentForm>(DEFAULT_PAYMENT);
  const [paymentSaving,  setPaymentSaving]  = useState(false);
  const [paymentError,   setPaymentError]   = useState('');

  // Contact modal (datos del admin de la empresa)
  const [contactCompany, setContactCompany] = useState<CompanyWithStats | null>(null);
  const [contactInfo,    setContactInfo]    = useState<{
    full_name: string | null;
    email:     string | null;
    job_title: string | null;
    phone:     string | null;
  } | null>(null);
  const [contactPhoneCopied, setContactPhoneCopied] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactCopied,  setContactCopied]  = useState(false);

  // Payment history modal
  const [historyCompany,  setHistoryCompany]  = useState<CompanyWithStats | null>(null);
  const [paymentsHistory, setPaymentsHistory] = useState<Payment[]>([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);

  // Delete company
  const [deleteCompany,     setDeleteCompany]     = useState<CompanyWithStats | null>(null);
  const [deleteStep,        setDeleteStep]        = useState<1 | 2>(1);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteSaving,      setDeleteSaving]      = useState(false);

  // Reset trial
  const [resetTrialId, setResetTrialId] = useState<string | null>(null);

  // Layout Pro
  const [layoutRequests,  setLayoutRequests]  = useState<LayoutRequestAdmin[]>([]);
  const [layoutLoading,   setLayoutLoading]   = useState(true);
  const [layoutModal,     setLayoutModal]     = useState<LayoutRequestAdmin | null>(null);
  const [layoutNewStatus, setLayoutNewStatus] = useState<LayoutRequest['status']>('pendiente');
  const [layoutFile,      setLayoutFile]      = useState<File | null>(null);
  const [layoutSaving,    setLayoutSaving]    = useState(false);
  const layoutFileRef = useRef<HTMLInputElement>(null);

  // Trial extension requests
  const [trialExtReqs,     setTrialExtReqs]     = useState<TrialExtensionRequest[]>([]);
  const [trialExtLoading,  setTrialExtLoading]  = useState(true);
  const [trialExtActing,   setTrialExtActing]   = useState<string | null>(null);

  // Promo codes
  const [promoCodes,       setPromoCodes]       = useState<PromoCode[]>([]);
  const [promoLoading,     setPromoLoading]      = useState(true);
  const [promoModalOpen,   setPromoModalOpen]    = useState(false);
  const [promoEditTarget,  setPromoEditTarget]   = useState<PromoCode | null>(null);
  const [promoForm,        setPromoForm]         = useState<PromoCodeForm>({ code: '', discount_percentage: '', description: '', max_uses: '', expires_at: '' });
  const [promoSaving,      setPromoSaving]       = useState(false);
  const [promoFormError,   setPromoFormError]    = useState('');
  const [promoDeleteTarget, setPromoDeleteTarget] = useState<PromoCode | null>(null);
  const [promoDeleting,    setPromoDeleting]     = useState(false);
  const [promoToggling,    setPromoToggling]     = useState<string | null>(null);

  // Invitations log
  const [invitations,      setInvitations]      = useState<InvitationLog[]>([]);
  const [showInvitations,  setShowInvitations]  = useState(false);

  // MRR history chart
  const [mrrHistory, setMrrHistory] = useState<{ month: string; amount: number }[]>([]);

  // Actividad por empresa (últimos 30 días) — top 3 módulos más usados
  const [activityByCompany, setActivityByCompany] = useState<Record<string, { type: string; count: number }[]>>({});
  const [activityLoading,   setActivityLoading]   = useState(false);
  const [lastActivityByCompany, setLastActivityByCompany] = useState<Record<string, string>>({});

  // Primer pago completado por empresa (para analytics de conversión trial→pago)
  const [firstPaymentByCompany, setFirstPaymentByCompany] = useState<Record<string, string>>({});

  // ── Data ──────────────────────────────────────────────────────────────────

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_superadmin_company_stats');
    if (error) console.error('[SuperAdmin]', error.message);
    setCompanies((data as CompanyWithStats[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  // Carga no bloqueante: corre en paralelo y no retrasa el render de la tabla.
  useEffect(() => {
    if (companies.length === 0) { setActivityByCompany({}); return; }

    let cancelled = false;
    setActivityLoading(true);

    const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const companyIds = companies.map((c) => c.id);

    supabase
      .from('audit_logs')
      .select('company_id, entity_type, created_at')
      .in('company_id', companyIds)
      .gte('created_at', since)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('[SuperAdmin] activity', error.message);
          setActivityLoading(false);
          return;
        }
        const counts: Record<string, Record<string, number>> = {};
        const lastAct: Record<string, string> = {};
        (data as { company_id: string; entity_type: string; created_at: string }[] ?? []).forEach((row) => {
          const byType = counts[row.company_id] ?? (counts[row.company_id] = {});
          byType[row.entity_type] = (byType[row.entity_type] ?? 0) + 1;
          if (!lastAct[row.company_id] || row.created_at > lastAct[row.company_id]) {
            lastAct[row.company_id] = row.created_at;
          }
        });
        const top: Record<string, { type: string; count: number }[]> = {};
        Object.entries(counts).forEach(([companyId, byType]) => {
          top[companyId] = Object.entries(byType)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 3);
        });
        setActivityByCompany(top);
        setLastActivityByCompany(lastAct);
        setActivityLoading(false);
      });

    return () => { cancelled = true; };
  }, [companies]);

  const fetchLayoutRequests = useCallback(async () => {
    setLayoutLoading(true);
    const { data } = await supabase
      .from('layout_requests')
      .select('*, companies:company_id ( name )')
      .order('created_at', { ascending: false });
    setLayoutRequests((data as LayoutRequestAdmin[]) ?? []);
    setLayoutLoading(false);
  }, []);

  useEffect(() => { fetchLayoutRequests(); }, [fetchLayoutRequests]);

  const fetchTrialExtReqs = useCallback(async () => {
    setTrialExtLoading(true);
    const { data } = await supabase
      .from('trial_extension_requests')
      .select('*, companies:company_id ( name, ruc, profiles ( job_title, full_name ) )')
      .order('requested_at', { ascending: false });
    setTrialExtReqs((data as TrialExtensionRequest[]) ?? []);
    setTrialExtLoading(false);
  }, []);

  useEffect(() => { fetchTrialExtReqs(); }, [fetchTrialExtReqs]);

  const fetchPromoCodes = useCallback(async () => {
    setPromoLoading(true);
    const { data } = await supabase
      .from('promo_codes')
      .select('*')
      .order('created_at', { ascending: false });
    setPromoCodes((data as PromoCode[]) ?? []);
    setPromoLoading(false);
  }, []);

  useEffect(() => { fetchPromoCodes(); }, [fetchPromoCodes]);

  const fetchInvitations = useCallback(async () => {
    const { data: invData, error: invError } = await supabase
      .from('invitations')
      .select(`
        id, email, role, created_at, accepted_at, invited_by, expires_at,
        companies!inner(name)
      `)
      .order('created_at', { ascending: false })
      .limit(100);
    if (invError) console.error('Error al cargar invitaciones:', invError.message);

    if (invData) {
      // invited_by referencia auth.users (no profiles), así que PostgREST no
      // puede incrustar el perfil: el correo del invitador se resuelve aparte.
      const inviterIds = Array.from(new Set(invData.map((i: any) => i.invited_by).filter(Boolean)));
      const emailById: Record<string, string> = {};
      if (inviterIds.length > 0) {
        const { data: inviters } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', inviterIds);
        (inviters ?? []).forEach((p: any) => { if (p.email) emailById[p.id] = p.email; });
      }
      setInvitations(invData.map((i: any) => ({
        id: i.id,
        email: i.email,
        role: i.role,
        company_name: i.companies?.name ?? '—',
        invited_by_email: (i.invited_by && emailById[i.invited_by]) || '—',
        created_at: i.created_at,
        accepted_at: i.accepted_at,
        // Columna real (default now() + 7 días); respaldo por si viniera NULL
        expires_at: i.expires_at ?? new Date(new Date(i.created_at).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      })));
    }
  }, []);

  useEffect(() => { fetchInvitations(); }, [fetchInvitations]);

  const fetchMrrHistory = useCallback(async () => {
    const { data: paymentsData } = await supabase
      .from('payments')
      .select('amount, created_at')
      .eq('status', 'completed')
      .gte('created_at', new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: true });

    const byMonth: Record<string, number> = {};
    if (paymentsData) {
      paymentsData.forEach((p: any) => {
        const month = new Date(p.created_at).toLocaleDateString('es-EC', { month: 'short', year: '2-digit' });
        byMonth[month] = (byMonth[month] ?? 0) + (p.amount ?? 0);
      });
    }
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const label = d.toLocaleDateString('es-EC', { month: 'short', year: '2-digit' });
      months.push({ month: label, amount: byMonth[label] ?? 0 });
    }
    setMrrHistory(months);
  }, []);

  useEffect(() => { fetchMrrHistory(); }, [fetchMrrHistory]);

  // Primer pago completado por empresa — usado por Analytics para tiempo trial→pago y tasa de conversión.
  const fetchConversionData = useCallback(async () => {
    const { data } = await supabase
      .from('payments')
      .select('company_id, payment_date')
      .eq('status', 'completed')
      .order('payment_date', { ascending: true });

    const first: Record<string, string> = {};
    (data as { company_id: string; payment_date: string }[] ?? []).forEach((p) => {
      if (!first[p.company_id]) first[p.company_id] = p.payment_date;
    });
    setFirstPaymentByCompany(first);
  }, []);

  useEffect(() => { fetchConversionData(); }, [fetchConversionData]);

  const fetchPaymentHistory = useCallback(async (companyId: string) => {
    setHistoryLoading(true);
    const { data } = await supabase
      .from('payments')
      .select('*')
      .eq('company_id', companyId)
      .order('payment_date', { ascending: false });
    setPaymentsHistory((data as Payment[]) ?? []);
    setHistoryLoading(false);
  }, []);

  // ── Metrics ───────────────────────────────────────────────────────────────

  const activePayCount = companies.filter((c) => c.payment_status === 'active').length;
  const trialCount     = companies.filter((c) => getStatus(c) === 'trial').length;
  const overdueCount   = companies.filter((c) => c.payment_status === 'overdue').length;
  const mrrEstimated   = computeMRR(companies);
  const mrrConfirmed   = computeMRR(companies.filter((c) => c.payment_status === 'active'));
  const totalUsers     = companies.reduce((s, c) => s + Number(c.user_count), 0);

  // ── Alertas proactivas ───────────────────────────────────────────────────

  const alertTrialEndingSoon = useMemo(() =>
    companies.filter((c) => {
      if (c.subscription_status !== 'trial' || !c.trial_ends_at) return false;
      const d = daysUntil(c.trial_ends_at);
      return d !== null && d >= 0 && d <= 1;
    }), [companies]);

  const alertNoActivity = useMemo(() =>
    companies.filter((c) => {
      if (!c.active) return false;
      const lastAct = lastActivityByCompany[c.id] ?? c.created_at;
      const d = daysSince(lastAct);
      return d !== null && d > 7;
    }), [companies, lastActivityByCompany]);

  const alertTrialExpired = useMemo(() =>
    companies.filter((c) => {
      if (c.subscription_status !== 'trial' || !c.trial_ends_at) return false;
      const d = daysUntil(c.trial_ends_at);
      return d !== null && d < 0;
    }), [companies]);

  const alertUpcomingCharge = useMemo(() =>
    companies.filter((c) => {
      if (c.subscription_status !== 'active' || !c.current_period_end) return false;
      const d = daysUntil(c.current_period_end);
      return d !== null && d >= 0 && d <= 3;
    }), [companies]);

  const totalAlerts = alertTrialEndingSoon.length + alertNoActivity.length
    + alertTrialExpired.length + alertUpcomingCharge.length;

  const scrollToCompany = (companyId: string) => {
    const el = document.querySelector(`[data-company-row="${companyId}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('sa-row-highlight');
    setTimeout(() => el.classList.remove('sa-row-highlight'), 2000);
  };

  // ── Analytics de conversión ───────────────────────────────────────────────

  const analytics = useMemo(() => {
    const trialToPayDays: number[] = [];
    companies.forEach((c) => {
      const firstPay = firstPaymentByCompany[c.id];
      if (!firstPay) return;
      const d = Math.floor((new Date(firstPay).getTime() - new Date(c.created_at).getTime()) / 86_400_000);
      if (d >= 0) trialToPayDays.push(d);
    });
    const avgTrialToPayDays = trialToPayDays.length
      ? Math.round(trialToPayDays.reduce((a, b) => a + b, 0) / trialToPayDays.length)
      : null;

    const companiesWithPayment = companies.filter((c) => firstPaymentByCompany[c.id]).length;
    const conversionRate = companies.length ? (companiesWithPayment / companies.length) * 100 : 0;

    const byPlan: Record<string, { count: number; mrr: number }> = {};
    companies.filter((c) => c.active).forEach((c) => {
      const entry = byPlan[c.plan] ?? (byPlan[c.plan] = { count: 0, mrr: 0 });
      entry.count += 1;
      entry.mrr += planPrice(c.plan, c.billing_cycle);
    });

    const upcoming = companies.filter((c) => {
      if (c.subscription_status !== 'active' || !c.current_period_end) return false;
      const d = daysUntil(c.current_period_end);
      return d !== null && d >= 0 && d <= 30;
    });
    const upcomingTotal = upcoming.reduce((s, c) => s + planPrice(c.plan, c.billing_cycle), 0);

    return { avgTrialToPayDays, conversionRate, companiesWithPayment, byPlan, upcoming, upcomingTotal };
  }, [companies, firstPaymentByCompany]);

  // ── Toast ─────────────────────────────────────────────────────────────────

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ── New company ───────────────────────────────────────────────────────────

  const handleNewCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newForm.name.trim()) { setSaveError('El nombre de la empresa es obligatorio.'); return; }
    setSaving(true);
    setSaveError('');

    const limits   = PLAN_LIMITS[newForm.plan];
    const now      = new Date();
    const trialEnd = newForm.trial_days > 0 ? addDays(now, newForm.trial_days).toISOString() : null;
    const billStart = trialEnd ? new Date(trialEnd) : now;
    const nextBill  = addDays(billStart, newForm.billing_cycle === 'annual' ? 365 : 30).toISOString();

    const { error } = await supabase.from('companies').insert({
      name:            newForm.name.trim(),
      ruc:             newForm.ruc.trim() || null,
      country:         newForm.country,
      plan:            newForm.plan,
      billing_cycle:   newForm.billing_cycle,
      active:          true,
      trial_ends_at:   trialEnd,
      next_billing_at: nextBill,
      max_warehouses:  limits.max_warehouses,
      max_products:    limits.max_products,
      max_users:       limits.max_users,
      payment_status:  trialEnd ? 'trial' : 'active',
    });

    if (error) {
      setSaveError('Error al crear empresa: ' + error.message);
    } else {
      setNewOpen(false);
      setNewForm(DEFAULT_FORM);
      showToast('Empresa creada correctamente.');
      fetchCompanies();
    }
    setSaving(false);
  };

  // ── Edit plan ─────────────────────────────────────────────────────────────

  const openEditPlan = (c: CompanyWithStats) => {
    setEditCompany(c);
    setEditPlan(c.plan);
    setEditCycle(c.billing_cycle);
    setEditNextBilling(c.next_billing_at ? diaEc(c.next_billing_at) : '');
  };

  const handleEditPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCompany) return;
    setEditSaving(true);

    const limits = PLAN_LIMITS[editPlan];
    const { error } = await supabase
      .from('companies')
      .update({
        plan:            editPlan,
        billing_cycle:   editCycle,
        // El día elegido, a la medianoche de Ecuador (antes: medianoche UTC, que se veía como el día anterior).
        next_billing_at: editNextBilling ? inicioDiaEc(editNextBilling) : null,
        max_warehouses:  limits.max_warehouses,
        max_products:    limits.max_products,
        max_users:       limits.max_users,
      })
      .eq('id', editCompany.id);

    if (error) {
      showToast('Error al actualizar plan: ' + error.message);
    } else {
      setEditCompany(null);
      showToast('Plan actualizado correctamente.');
      fetchCompanies();
    }
    setEditSaving(false);
  };

  // ── Toggle active ─────────────────────────────────────────────────────────

  const toggleActive = async (c: CompanyWithStats) => {
    setTogglingId(c.id);
    const { error } = await supabase
      .from('companies')
      .update({ active: !c.active })
      .eq('id', c.id);

    if (error) {
      showToast('Error al cambiar estado.');
    } else {
      setCompanies((prev) =>
        prev.map((co) => (co.id === c.id ? { ...co, active: !co.active } : co)),
      );
      showToast(c.active ? 'Empresa desactivada.' : 'Empresa activada.');
    }
    setTogglingId(null);
  };

  // ── Edit company name (inline) ────────────────────────────────────────────

  const startEditName = (c: CompanyWithStats) => {
    setEditingNameId(c.id);
    setEditingNameValue(c.name);
  };

  const cancelEditName = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const saveEditName = async (c: CompanyWithStats) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) { showToast('El nombre no puede estar vacío'); return; }

    setSavingNameId(c.id);
    const { error } = await supabase
      .from('companies')
      .update({ name: trimmed })
      .eq('id', c.id);

    if (error) {
      showToast('Error al actualizar nombre: ' + error.message);
    } else {
      setEditingNameId(null);
      setEditingNameValue('');
      showToast('Nombre actualizado correctamente');
      fetchCompanies();
    }
    setSavingNameId(null);
  };

  // ── Edit company RUC (inline) ─────────────────────────────────────────────

  const startEditRuc = (c: CompanyWithStats) => {
    setEditingRucId(c.id);
    setEditingRucValue(c.ruc ?? '');
  };

  const cancelEditRuc = () => {
    setEditingRucId(null);
    setEditingRucValue('');
  };

  const saveEditRuc = async (c: CompanyWithStats) => {
    const trimmed = editingRucValue.trim();
    const rucErr = validateRucEdit(trimmed);
    if (rucErr) { showToast(rucErr); return; }

    setSavingRucId(c.id);
    const { error } = await supabase
      .from('companies')
      .update({ ruc: trimmed })
      .eq('id', c.id);

    if (error) {
      showToast('Error al actualizar RUC: ' + error.message);
    } else {
      setEditingRucId(null);
      setEditingRucValue('');
      showToast('RUC actualizado correctamente');
      fetchCompanies();
    }
    setSavingRucId(null);
  };

  // ── Contact modal ─────────────────────────────────────────────────────────

  const fetchAdminContact = useCallback(async (companyId: string) => {
    setContactLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('full_name, email, job_title, phone')
      .eq('company_id', companyId)
      .eq('role', 'admin')
      .limit(1)
      .maybeSingle();

    if (error) console.error('[SuperAdmin] contact', error.message);
    setContactInfo(data
      ? { full_name: data.full_name, email: data.email, job_title: data.job_title, phone: data.phone }
      : null);
    setContactLoading(false);
  }, []);

  const openContactModal = (c: CompanyWithStats) => {
    setContactCompany(c);
    setContactInfo(null);
    setContactCopied(false);
    setContactPhoneCopied(false);
    fetchAdminContact(c.id);
  };

  const handleCopyEmail = async () => {
    if (!contactInfo?.email) return;
    try {
      await navigator.clipboard.writeText(contactInfo.email);
      setContactCopied(true);
      setTimeout(() => setContactCopied(false), 2000);
    } catch {
      showToast('No se pudo copiar el correo.');
    }
  };

  const handleCopyPhone = async () => {
    if (!contactInfo?.phone) return;
    try {
      await navigator.clipboard.writeText(contactInfo.phone);
      setContactPhoneCopied(true);
      setTimeout(() => setContactPhoneCopied(false), 2000);
    } catch {
      showToast('No se pudo copiar el teléfono.');
    }
  };

  const contactToPayment = () => {
    const co = contactCompany;
    setContactCompany(null);
    if (co) openPaymentModal(co);
  };

  // ── Register payment ──────────────────────────────────────────────────────

  const openPaymentModal = (c: CompanyWithStats) => {
    setPaymentCompany(c);
    setPaymentForm({
      ...DEFAULT_PAYMENT,
      payment_date: hoyEc(),
    });
    setPaymentError('');
  };

  const handleRegisterPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentCompany) return;
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) { setPaymentError('Ingresa un monto válido.'); return; }
    if (!paymentForm.payment_date) { setPaymentError('Selecciona la fecha de pago.'); return; }

    setPaymentSaving(true);
    setPaymentError('');

    const { data: userData } = await supabase.auth.getUser();
    // Próximo cobro: 30 días de calendario después del pago, a la medianoche de Ecuador (antes: new Date('AAAA-MM-DD')
    // es medianoche UTC y el próximo cobro se veía un día antes).
    const nextPayDate = inicioDiaEc(sumarDiasDia(paymentForm.payment_date, 30));

    const { error: payErr } = await supabase.from('payments').insert({
      company_id:   paymentCompany.id,
      amount,
      payment_date: paymentForm.payment_date,
      method:       paymentForm.method,
      reference:    paymentForm.reference.trim() || null,
      created_by:   userData.user?.id ?? null,
      notes:        paymentForm.notes.trim() || null,
    });

    if (payErr) {
      setPaymentError('Error al registrar pago: ' + payErr.message);
      setPaymentSaving(false);
      return;
    }

    const { error: coErr } = await supabase
      .from('companies')
      .update({
        payment_status:    'active',
        last_payment_date: inicioDiaEc(paymentForm.payment_date),
        next_payment_date: nextPayDate,
      })
      .eq('id', paymentCompany.id);

    if (coErr) {
      showToast('Pago registrado pero error al actualizar estado: ' + coErr.message);
    } else {
      showToast(`Pago de $${amount.toLocaleString('es-CL')} registrado. Estado → Activo.`);
    }

    setPaymentCompany(null);
    fetchCompanies();
    setPaymentSaving(false);
  };

  // ── Payment history ───────────────────────────────────────────────────────

  const openPaymentHistory = (c: CompanyWithStats) => {
    setHistoryCompany(c);
    fetchPaymentHistory(c.id);
  };

  // ── Delete company ────────────────────────────────────────────────────────

  const openDelete = (c: CompanyWithStats) => {
    setDeleteCompany(c);
    setDeleteStep(1);
    setDeleteConfirmName('');
  };

  const handleDeleteCompany = async () => {
    if (!deleteCompany) return;
    if (deleteConfirmName.trim().toLowerCase() !== deleteCompany.name.trim().toLowerCase()) {
      showToast('El nombre no coincide.');
      return;
    }
    setDeleteSaving(true);

    const { error } = await supabase.rpc('delete_company_cascade', {
      p_company_id: deleteCompany.id,
    });

    if (error) {
      showToast('Error al eliminar empresa: ' + error.message);
    } else {
      showToast(`Empresa "${deleteCompany.name}" eliminada permanentemente.`);
      setDeleteCompany(null);
      fetchCompanies();
    }
    setDeleteSaving(false);
  };

  // ── Reset trial ───────────────────────────────────────────────────────────

  const handleResetTrial = async (c: CompanyWithStats) => {
    setResetTrialId(c.id);
    const newTrialEnd = addDays(new Date(), 5).toISOString();
    const { error } = await supabase
      .from('companies')
      .update({
        trial_ends_at:  newTrialEnd,
        payment_status: 'trial',
      })
      .eq('id', c.id);

    if (error) {
      showToast('Error al resetear trial: ' + error.message);
    } else {
      showToast(`Trial de "${c.name}" extendido 5 días.`);
      fetchCompanies();
    }
    setResetTrialId(null);
  };

  // ── Promo code handlers ───────────────────────────────────────────────────

  const openPromoCreate = () => {
    setPromoEditTarget(null);
    setPromoForm({ code: '', discount_percentage: '', description: '', max_uses: '', expires_at: '' });
    setPromoFormError('');
    setPromoModalOpen(true);
  };

  const openPromoEdit = (p: PromoCode) => {
    setPromoEditTarget(p);
    setPromoForm({
      code:                p.code,
      discount_percentage: String(p.discount_percentage),
      description:         p.description ?? '',
      max_uses:            p.max_uses != null ? String(p.max_uses) : '',
      expires_at:          p.expires_at ? diaEc(p.expires_at) : '',
    });
    setPromoFormError('');
    setPromoModalOpen(true);
  };

  const handlePromoSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = promoForm.code.trim().toUpperCase().replace(/\s/g, '');
    if (!code) { setPromoFormError('El código es obligatorio.'); return; }
    const pct = parseInt(promoForm.discount_percentage);
    if (!pct || pct < 1 || pct > 100) { setPromoFormError('El descuento debe ser entre 1 y 100.'); return; }

    setPromoSaving(true);
    setPromoFormError('');

    const payload: Record<string, any> = {
      code,
      discount_percentage: pct,
      description:         promoForm.description.trim() || null,
      max_uses:            promoForm.max_uses ? parseInt(promoForm.max_uses) || null : null,
      expires_at:          promoForm.expires_at ? finDiaEc(promoForm.expires_at) : null,
    };

    let error: any;
    if (promoEditTarget) {
      ({ error } = await supabase.from('promo_codes').update(payload).eq('id', promoEditTarget.id));
    } else {
      payload.active = true;
      payload.uses_count = 0;
      ({ error } = await supabase.from('promo_codes').insert(payload));
    }

    if (error) {
      setPromoFormError(error.message.includes('unique') ? 'Ese código ya existe.' : 'Error: ' + error.message);
    } else {
      setPromoModalOpen(false);
      showToast(promoEditTarget ? 'Código actualizado.' : 'Código creado correctamente.');
      fetchPromoCodes();
    }
    setPromoSaving(false);
  };

  const handlePromoToggle = async (p: PromoCode) => {
    setPromoToggling(p.id);
    const { error } = await supabase
      .from('promo_codes')
      .update({ active: !p.active })
      .eq('id', p.id);
    if (error) {
      showToast('Error al cambiar estado.');
    } else {
      setPromoCodes((prev) => prev.map((x) => x.id === p.id ? { ...x, active: !p.active } : x));
      showToast(p.active ? 'Código desactivado.' : 'Código activado.');
    }
    setPromoToggling(null);
  };

  const handlePromoDelete = async () => {
    if (!promoDeleteTarget) return;
    setPromoDeleting(true);
    const { error } = await supabase.from('promo_codes').delete().eq('id', promoDeleteTarget.id);
    if (error) {
      showToast('Error al eliminar: ' + error.message);
    } else {
      showToast(`Código "${promoDeleteTarget.code}" eliminado.`);
      setPromoDeleteTarget(null);
      fetchPromoCodes();
    }
    setPromoDeleting(false);
  };

  // ── Trial extension handlers ──────────────────────────────────────────────

  const handleTrialExtAction = async (req: TrialExtensionRequest, action: 'approved' | 'rejected') => {
    setTrialExtActing(req.id);
    const now = new Date().toISOString();
    const updates: Record<string, any> = { status: action, reviewed_at: now };

    if (action === 'approved') {
      const { error: trialErr } = await supabase
        .from('companies')
        .update({ trial_ends_at: new Date(Date.now() + 5 * 86_400_000).toISOString() })
        .eq('id', req.company_id);
      if (trialErr) {
        showToast('Error al extender trial: ' + trialErr.message);
        setTrialExtActing(null);
        return;
      }
    }

    const { error: reqErr } = await supabase
      .from('trial_extension_requests')
      .update(updates)
      .eq('id', req.id);
    if (reqErr) {
      showToast('Error al actualizar solicitud: ' + reqErr.message);
      setTrialExtActing(null);
      return;
    }

    showToast(action === 'approved' ? 'Trial extendido 5 días.' : 'Solicitud rechazada.');
    fetchTrialExtReqs();
    setTrialExtActing(null);
  };

  // ── Layout Pro handlers ───────────────────────────────────────────────────

  const openLayoutModal = (req: LayoutRequestAdmin) => {
    setLayoutModal(req);
    setLayoutNewStatus(req.status);
    setLayoutFile(null);
    if (layoutFileRef.current) layoutFileRef.current.value = '';
  };

  const handleLayoutStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!layoutModal) return;
    setLayoutSaving(true);

    let layoutUrl = layoutModal.layout_url;

    if (layoutFile) {
      const ext  = layoutFile.name.split('.').pop() ?? 'pdf';
      const path = `layouts/${layoutModal.id}/${nombreSeguroStorage(`${Date.now()}.${ext}`)}`;
      const { error: upErr } = await supabase.storage
        .from('layout-files')
        .upload(path, layoutFile, { upsert: false });
      if (upErr) { showToast('Error al subir archivo: ' + upErr.message); setLayoutSaving(false); return; }
      const { data: pub } = supabase.storage.from('layout-files').getPublicUrl(path);
      layoutUrl = pub.publicUrl;
    }

    const updates: Record<string, any> = {
      status:     layoutNewStatus,
      layout_url: layoutUrl,
    };
    if (layoutNewStatus === 'entregado' && !layoutModal.delivered_at) {
      updates.delivered_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('layout_requests')
      .update(updates)
      .eq('id', layoutModal.id);

    if (error) {
      showToast('Error al actualizar: ' + error.message);
    } else {
      showToast('Solicitud actualizada correctamente.');
      setLayoutModal(null);
      fetchLayoutRequests();
    }
    setLayoutSaving(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="sa-root">

      {/* ── Global metrics ── */}
      <div className="sa-metrics-grid">
        <MetricCard
          label="Pago confirmado"
          value={activePayCount}
          icon={<BuildingIcon />}
          color="#22c55e"
          bg="rgba(34,197,94,0.1)"
        />
        <MetricCard
          label="En trial"
          value={trialCount}
          icon={<ClockIcon />}
          color="#f59e0b"
          bg="rgba(245,158,11,0.1)"
        />
        <MetricCard
          label="Vencidas / Morosas"
          value={overdueCount}
          icon={<AlertIcon />}
          color="#ef4444"
          bg="rgba(239,68,68,0.1)"
        />
        <MetricCard
          label="MRR confirmado"
          value={`$${Math.round(mrrConfirmed).toLocaleString('es-CL')}`}
          icon={<DollarIcon />}
          color="#22c55e"
          bg="rgba(34,197,94,0.1)"
          sub="/mes"
        />
        <MetricCard
          label="MRR estimado"
          value={`$${Math.round(mrrEstimated).toLocaleString('es-CL')}`}
          icon={<DollarIcon />}
          color="#64748b"
          bg="rgba(100,116,139,0.1)"
          sub="/mes"
        />
        <MetricCard
          label="Total usuarios"
          value={totalUsers}
          icon={<UsersIcon />}
          color="#3b82f6"
          bg="rgba(59,130,246,0.1)"
        />
      </div>

      {/* ── Alertas proactivas ── */}
      {totalAlerts > 0 && (
        <div className="sa-section sa-alerts-panel">
          <div className="sa-section-header">
            <div>
              <h3 className="sa-section-title">Alertas</h3>
              <p className="sa-section-sub">{totalAlerts} alerta{totalAlerts !== 1 ? 's' : ''} requieren atención</p>
            </div>
          </div>

          <div className="sa-alerts-list">
            {alertTrialEndingSoon.length > 0 && (
              <div className="sa-alert-group">
                <p className="sa-alert-group-title">🔴 Trial vence hoy o mañana</p>
                {alertTrialEndingSoon.map((c) => (
                  <div key={c.id} className="sa-alert-row">
                    <span className="sa-alert-company">{c.name}</span>
                    <span className="sa-alert-date">vence {fmtDate(c.trial_ends_at)}</span>
                    <button className="sa-btn-secondary sa-alert-action" onClick={() => handleResetTrial(c)}>
                      Extender
                    </button>
                  </div>
                ))}
              </div>
            )}

            {alertNoActivity.length > 0 && (
              <div className="sa-alert-group">
                <p className="sa-alert-group-title">🟡 Sin actividad +7 días</p>
                {alertNoActivity.map((c) => (
                  <div key={c.id} className="sa-alert-row">
                    <span className="sa-alert-company">{c.name}</span>
                    <span className="sa-alert-date">
                      última actividad {fmtDate(lastActivityByCompany[c.id] ?? c.created_at)}
                    </span>
                    <button className="sa-btn-secondary sa-alert-action" onClick={() => scrollToCompany(c.id)}>
                      Ver empresa
                    </button>
                  </div>
                ))}
              </div>
            )}

            {alertTrialExpired.length > 0 && (
              <div className="sa-alert-group">
                <p className="sa-alert-group-title">🟠 Trial vencido sin pagar</p>
                {alertTrialExpired.map((c) => (
                  <div key={c.id} className="sa-alert-row">
                    <span className="sa-alert-company">{c.name}</span>
                    <span className="sa-alert-date">venció {fmtDate(c.trial_ends_at)}</span>
                    <button className="sa-btn-secondary sa-alert-action" onClick={() => openContactModal(c)}>
                      Contactar
                    </button>
                  </div>
                ))}
              </div>
            )}

            {alertUpcomingCharge.length > 0 && (
              <div className="sa-alert-group">
                <p className="sa-alert-group-title">🟢 Cobro próximo en 3 días</p>
                {alertUpcomingCharge.map((c) => (
                  <div key={c.id} className="sa-alert-row">
                    <span className="sa-alert-company">{c.name}</span>
                    <span className="sa-alert-date">cobro {fmtDate(c.current_period_end)}</span>
                    <button className="sa-btn-secondary sa-alert-action" onClick={() => openPaymentHistory(c)}>
                      Ver historial
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MRR histórico ── */}
      <div className="sa-section" style={{ padding: '20px 24px' }}>
        <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
          MRR últimos 6 meses
        </p>
        {mrrHistory.every(m => m.amount === 0) ? (
          <p style={{ textAlign: 'center', color: '#475569', fontSize: '0.875rem', padding: '24px 0' }}>
            Sin pagos confirmados aún
          </p>
        ) : (() => {
          const maxVal = Math.max(...mrrHistory.map(m => m.amount), 1);
          const BAR_W = 36;
          const GAP   = 20;
          const H     = 100;
          const LABEL_H = 28;
          const totalW = mrrHistory.length * (BAR_W + GAP) - GAP;
          return (
            <svg
              viewBox={`0 0 ${totalW} ${H + LABEL_H}`}
              style={{ width: '100%', maxWidth: 560, display: 'block', margin: '0 auto' }}
            >
              {mrrHistory.map((m, i) => {
                const barH = Math.max(4, Math.round((m.amount / maxVal) * H));
                const x = i * (BAR_W + GAP);
                const y = H - barH;
                return (
                  <g key={m.month}>
                    <rect x={x} y={y} width={BAR_W} height={barH} rx={4} fill="#22c55e" opacity={m.amount === 0 ? 0.2 : 1} />
                    {m.amount > 0 && (
                      <text x={x + BAR_W / 2} y={y - 5} textAnchor="middle" fontSize={9} fill="#22c55e" fontWeight={700}>
                        ${m.amount >= 1000 ? (m.amount / 1000).toFixed(1) + 'k' : m.amount}
                      </text>
                    )}
                    <text x={x + BAR_W / 2} y={H + LABEL_H - 4} textAnchor="middle" fontSize={9} fill="#64748b">
                      {m.month}
                    </text>
                  </g>
                );
              })}
            </svg>
          );
        })()}
      </div>

      {/* ── Analytics de conversión ── */}
      <div className="sa-section">
        <div className="sa-section-header">
          <div>
            <h3 className="sa-section-title">Analytics</h3>
            <p className="sa-section-sub">Conversión trial → pago y desglose de MRR</p>
          </div>
        </div>

        <div className="sa-analytics-grid">
          <div className="sa-analytics-card">
            <span className="sa-analytics-value">
              {analytics.avgTrialToPayDays !== null ? `${analytics.avgTrialToPayDays}d` : '—'}
            </span>
            <span className="sa-analytics-label">Tiempo promedio trial → pago</span>
          </div>
          <div className="sa-analytics-card">
            <span className="sa-analytics-value">{analytics.conversionRate.toFixed(1)}%</span>
            <span className="sa-analytics-label">
              Tasa de conversión ({analytics.companiesWithPayment}/{companies.length})
            </span>
          </div>
          <div className="sa-analytics-card">
            <span className="sa-analytics-value">${Math.round(analytics.upcomingTotal).toLocaleString('es-CL')}</span>
            <span className="sa-analytics-label">
              Próximos cobros (30d) · {analytics.upcoming.length} empresa{analytics.upcoming.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="sa-table-wrap" style={{ marginTop: 16 }}>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Empresas activas</th>
                <th>MRR</th>
              </tr>
            </thead>
            <tbody>
              {(['starter', 'professional', 'enterprise'] as const).map((p) => {
                const entry = analytics.byPlan[p] ?? { count: 0, mrr: 0 };
                return (
                  <tr key={p}>
                    <td>
                      <span className="sa-plan-badge" style={{ color: PLAN_COLORS[p], borderColor: PLAN_COLORS[p] + '50', background: PLAN_COLORS[p] + '12' }}>
                        {PLAN_LABELS[p]}
                      </span>
                    </td>
                    <td>{entry.count}</td>
                    <td style={{ color: '#22c55e', fontWeight: 700 }}>
                      ${Math.round(entry.mrr).toLocaleString('es-CL')}/mes
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Companies section ── */}
      <div className="sa-section">
        <div className="sa-section-header">
          <div>
            <h3 className="sa-section-title">Empresas registradas</h3>
            <p className="sa-section-sub">
              {companies.length} empresa{companies.length !== 1 ? 's' : ''} en total
              {activePayCount > 0 && ` · ${activePayCount} con pago confirmado`}
            </p>
          </div>
          <button
            className="sa-btn-primary"
            onClick={() => { setNewOpen(true); setSaveError(''); setNewForm(DEFAULT_FORM); }}
          >
            <PlusIcon /> Nueva empresa
          </button>
        </div>

        <div className="sa-table-wrap">
          {loading ? (
            <div className="sa-loading">
              <span className="sa-spinner" />
              <p>Cargando empresas…</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="sa-empty">
              <BuildingIcon />
              <p className="sa-empty-title">Sin empresas registradas</p>
              <p className="sa-empty-sub">Crea la primera empresa con el botón de arriba.</p>
            </div>
          ) : (
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Plan</th>
                  <th>Recursos</th>
                  <th>Estado</th>
                  <th>Pago</th>
                  <th>Actividad (30d)</th>
                  <th>Módulos activos</th>
                  <th>Último acceso</th>
                  <th>Registro</th>
                  <th style={{ width: 200 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => {
                  const status = getStatus(c);
                  return (
                    <tr key={c.id} data-company-row={c.id} className={c.active ? '' : 'sa-row-inactive'}>

                      <td>
                        <div className="sa-company-cell">
                          <div className="sa-company-avatar">{c.name[0].toUpperCase()}</div>
                          <div>
                            {editingNameId === c.id ? (
                              <div className="sa-name-edit">
                                <input
                                  type="text"
                                  className="sa-name-edit-input"
                                  value={editingNameValue}
                                  onChange={(e) => setEditingNameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEditName(c);
                                    if (e.key === 'Escape') cancelEditName();
                                  }}
                                  autoFocus
                                  disabled={savingNameId === c.id}
                                />
                                <button
                                  className="sa-btn-icon sa-btn-icon-green"
                                  title="Guardar"
                                  onClick={() => saveEditName(c)}
                                  disabled={savingNameId === c.id}
                                >
                                  {savingNameId === c.id ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                                </button>
                                <button
                                  className="sa-btn-icon"
                                  title="Cancelar"
                                  onClick={cancelEditName}
                                  disabled={savingNameId === c.id}
                                >
                                  <CloseIcon />
                                </button>
                              </div>
                            ) : (
                              <div className="sa-name-display">
                                <div className="sa-company-name">{c.name}</div>
                                <button
                                  className="sa-btn-icon sa-btn-icon-edit-name"
                                  title="Editar nombre"
                                  onClick={() => startEditName(c)}
                                >
                                  <PencilIcon />
                                </button>
                              </div>
                            )}
                            <div className="sa-company-meta">
                              {editingRucId === c.id ? (
                                <div className="sa-name-edit">
                                  <input
                                    type="text"
                                    className="sa-name-edit-input"
                                    value={editingRucValue}
                                    onChange={(e) => setEditingRucValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditRuc(c);
                                      if (e.key === 'Escape') cancelEditRuc();
                                    }}
                                    autoFocus
                                    disabled={savingRucId === c.id}
                                  />
                                  <button
                                    className="sa-btn-icon sa-btn-icon-green"
                                    title="Guardar"
                                    onClick={() => saveEditRuc(c)}
                                    disabled={savingRucId === c.id}
                                  >
                                    {savingRucId === c.id ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                                  </button>
                                  <button
                                    className="sa-btn-icon"
                                    title="Cancelar"
                                    onClick={cancelEditRuc}
                                    disabled={savingRucId === c.id}
                                  >
                                    <CloseIcon />
                                  </button>
                                </div>
                              ) : (
                                <span className="sa-name-display">
                                  RUC {c.ruc ?? '—'}
                                  <button
                                    className="sa-btn-icon sa-btn-icon-edit-name"
                                    title="Editar RUC"
                                    onClick={() => startEditRuc(c)}
                                  >
                                    <PencilIcon />
                                  </button>
                                </span>
                              )}
                              {c.country && <span>{c.country}</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <PlanCell plan={c.plan} cycle={c.billing_cycle} />
                      </td>

                      <td>
                        <div className="sa-resources">
                          <span title="Usuarios">
                            <UsersSmIcon /> {Number(c.user_count)}
                          </span>
                          <span title="Productos">
                            <BoxSmIcon /> {Number(c.product_count)}
                          </span>
                          <span title="Bodegas">
                            <WhSmIcon /> {Number(c.warehouse_count)}
                          </span>
                        </div>
                      </td>

                      <td>
                        <StatusCell status={status} trialEndsAt={c.trial_ends_at} />
                      </td>

                      <td>
                        <PaymentStatusCell
                          paymentStatus={c.payment_status}
                          lastPaymentDate={c.last_payment_date}
                          nextPaymentDate={c.next_payment_date}
                        />
                      </td>

                      <td>
                        {activityByCompany[c.id]?.length ? (
                          <div className="sa-activity-badges">
                            {activityByCompany[c.id].map((m) => (
                              <span key={m.type} className="sa-activity-badge" title={`${m.count} registros en 30 días`}>
                                {ACTIVITY_MODULE_LABELS[m.type] ?? m.type}
                              </span>
                            ))}
                          </div>
                        ) : activityLoading ? (
                          <span className="sa-activity-loading">Cargando…</span>
                        ) : (
                          <span className="sa-activity-empty">Sin actividad</span>
                        )}
                      </td>

                      <td>
                        <div className="sa-activity-badges">
                          {Number(c.product_count) > 0 && (
                            <span className="sa-activity-badge" title={`${c.product_count} productos`}>Productos</span>
                          )}
                          {Number(c.warehouse_count) > 0 && (
                            <span className="sa-activity-badge" title={`${c.warehouse_count} bodegas`}>Bodegas</span>
                          )}
                          {Number(c.worker_count) > 0 && (
                            <span className="sa-activity-badge" title={`${c.worker_count} trabajadores`}>SSO</span>
                          )}
                          {Number(c.product_count) === 0 && Number(c.warehouse_count) === 0 && Number(c.worker_count) === 0 && (
                            <span className="sa-activity-empty">Sin módulos</span>
                          )}
                        </div>
                      </td>

                      <td className="sa-td-date">
                        {c.admin_last_sign_in_at ? fmtDate(c.admin_last_sign_in_at) : <span style={{ color: '#334155' }}>Nunca</span>}
                      </td>

                      <td className="sa-td-date">{fmtDate(c.created_at)}</td>

                      <td>
                        <div className="sa-actions">
                          <button
                            className="sa-btn-icon"
                            title="Entrar al entorno de esta empresa"
                            onClick={() => onEnterCompany?.(c)}
                            disabled={!onEnterCompany}
                          >
                            <EyeIcon />
                          </button>
                          <button
                            className="sa-btn-icon"
                            title="Contactar administrador"
                            onClick={() => openContactModal(c)}
                          >
                            <ContactIcon />
                          </button>
                          <button
                            className="sa-btn-icon"
                            title="Cambiar plan"
                            onClick={() => openEditPlan(c)}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="sa-btn-icon sa-btn-icon-green"
                            title="Registrar pago"
                            onClick={() => openPaymentModal(c)}
                          >
                            <DollarSmIcon />
                          </button>
                          <button
                            className="sa-btn-icon"
                            title="Historial de pagos"
                            onClick={() => openPaymentHistory(c)}
                          >
                            <ListIcon />
                          </button>
                          <button
                            className="sa-btn-icon sa-btn-icon-amber"
                            title="Resetear trial (+5 días)"
                            onClick={() => handleResetTrial(c)}
                            disabled={resetTrialId === c.id}
                          >
                            {resetTrialId === c.id
                              ? <span className="sa-spinner sa-spinner-sm" />
                              : <ClockIcon />
                            }
                          </button>
                          <button
                            className={`sa-btn-toggle ${c.active ? 'deactivate' : 'activate'}`}
                            title={c.active ? 'Desactivar empresa' : 'Activar empresa'}
                            onClick={() => toggleActive(c)}
                            disabled={togglingId === c.id}
                          >
                            {togglingId === c.id
                              ? <span className="sa-spinner sa-spinner-sm" />
                              : c.active ? <BlockIcon /> : <CheckIcon />
                            }
                          </button>
                          <button
                            className="sa-btn-icon sa-btn-icon-red"
                            title="Eliminar empresa permanentemente"
                            onClick={() => openDelete(c)}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Layout Pro section ── */}
      <div className="sa-section">
        <div className="sa-section-header">
          <div>
            <h3 className="sa-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LayoutIcon />
              Solicitudes Layout Pro
              {layoutRequests.filter(r => r.status === 'pendiente').length > 0 && (
                <span className="sa-layout-pending-badge">
                  {layoutRequests.filter(r => r.status === 'pendiente').length} pendiente{layoutRequests.filter(r => r.status === 'pendiente').length !== 1 ? 's' : ''}
                </span>
              )}
            </h3>
            <p className="sa-section-sub">
              {layoutRequests.length} solicitud{layoutRequests.length !== 1 ? 'es' : ''} en total
            </p>
          </div>
        </div>

        <div className="sa-table-wrap">
          {layoutLoading ? (
            <div className="sa-loading"><span className="sa-spinner" /><p>Cargando solicitudes…</p></div>
          ) : layoutRequests.length === 0 ? (
            <div className="sa-empty">
              <LayoutIcon />
              <p className="sa-empty-title">Sin solicitudes</p>
              <p className="sa-empty-sub">Aquí aparecerán las solicitudes de diseño de los clientes.</p>
            </div>
          ) : (
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Empresa · Bodega</th>
                  <th>Dimensiones</th>
                  <th>Estado</th>
                  <th>Archivos</th>
                  <th>Fecha</th>
                  <th style={{ width: 90 }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {layoutRequests.map(req => {
                  const cfg  = STATUS_CFG[req.status];
                  const dims = req.dimensions as any;
                  return (
                    <tr key={req.id}>
                      <td>
                        <div className="sa-company-cell">
                          <div className="sa-company-avatar" style={{ background: 'rgba(139,92,246,0.2)', color: '#a78bfa' }}>
                            <LayoutIcon />
                          </div>
                          <div>
                            <div className="sa-company-name">{req.companies?.name ?? '—'}</div>
                            <div className="sa-company-meta">
                              <span>{req.warehouse_name ?? 'Sin bodega'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="sa-company-meta" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                          {dims.largo && <span>{dims.largo} × {dims.ancho} × {dims.altura} m</span>}
                          {dims.zonas?.length > 0 && <span>{dims.zonas.length} zona{dims.zonas.length !== 1 ? 's' : ''}</span>}
                        </div>
                      </td>
                      <td>
                        <span className="sa-status-badge"
                          style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.color + '40' }}
                        >
                          <span className="sa-status-dot" style={{ background: cfg.color }} />
                          {cfg.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {req.file_url && (
                            <EnlaceArchivo bucket="layout-files" valor={req.file_url} className="sa-layout-file-link">
                              Plano cliente
                            </EnlaceArchivo>
                          )}
                          {req.layout_url && (
                            <EnlaceArchivo bucket="layout-files" valor={req.layout_url} className="sa-layout-file-link sa-layout-file-delivered">
                              Layout entregado
                            </EnlaceArchivo>
                          )}
                          {!req.file_url && !req.layout_url && (
                            <span style={{ color: '#334155', fontSize: '0.78rem' }}>—</span>
                          )}
                        </div>
                      </td>
                      <td className="sa-td-date">{fmtDate(req.created_at)}</td>
                      <td>
                        <button
                          className="sa-btn-icon"
                          title="Cambiar estado / subir layout"
                          onClick={() => openLayoutModal(req)}
                        >
                          <EditIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Trial extension requests section ── */}
      <div className="sa-section">
        <div className="sa-section-header">
          <div>
            <h3 className="sa-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockIcon />
              Solicitudes de extensión de trial
              {trialExtReqs.filter(r => r.status === 'pending').length > 0 && (
                <span className="sa-layout-pending-badge">
                  {trialExtReqs.filter(r => r.status === 'pending').length} pendiente{trialExtReqs.filter(r => r.status === 'pending').length !== 1 ? 's' : ''}
                </span>
              )}
            </h3>
            <p className="sa-section-sub">
              {trialExtReqs.length} solicitud{trialExtReqs.length !== 1 ? 'es' : ''} en total
            </p>
          </div>
        </div>

        <div className="sa-table-wrap">
          {trialExtLoading ? (
            <div className="sa-loading"><span className="sa-spinner" /><p>Cargando…</p></div>
          ) : trialExtReqs.length === 0 ? (
            <div className="sa-empty">
              <ClockIcon />
              <p className="sa-empty-title">Sin solicitudes</p>
              <p className="sa-empty-sub">Aquí aparecerán las solicitudes de extensión de trial de los clientes.</p>
            </div>
          ) : (
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Solicitante / Cargo</th>
                  <th>Mensaje</th>
                  <th>Fecha solicitud</th>
                  <th>Estado</th>
                  <th style={{ width: 160 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {trialExtReqs.map((req) => {
                  const co      = req.companies;
                  const profile = co?.profiles?.[0];
                  const isPending = req.status === 'pending';
                  return (
                    <tr key={req.id} className={isPending ? '' : 'sa-row-inactive'}>
                      <td>
                        <div className="sa-company-cell">
                          <div className="sa-company-avatar">{(co?.name ?? '?')[0].toUpperCase()}</div>
                          <div>
                            <div className="sa-company-name">{co?.name ?? '—'}</div>
                            {co?.ruc && <div className="sa-company-meta"><span>RUC {co.ruc}</span></div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.84rem', color: '#cbd5e1' }}>{profile?.full_name ?? '—'}</div>
                        {profile?.job_title && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{profile.job_title}</div>
                        )}
                      </td>
                      <td style={{ maxWidth: 200 }}>
                        <span style={{ fontSize: '0.82rem', color: '#94a3b8', whiteSpace: 'pre-wrap' }}>
                          {req.user_message ?? <span style={{ color: '#334155' }}>—</span>}
                        </span>
                      </td>
                      <td className="sa-td-date">{fmtDate(req.requested_at)}</td>
                      <td>
                        <span
                          className="sa-status-badge"
                          style={
                            req.status === 'pending'
                              ? { color: '#f59e0b', background: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.3)' }
                              : req.status === 'approved'
                              ? { color: '#22c55e', background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)' }
                              : { color: '#f87171', background: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)' }
                          }
                        >
                          <span
                            className="sa-status-dot"
                            style={{ background: req.status === 'pending' ? '#f59e0b' : req.status === 'approved' ? '#22c55e' : '#f87171' }}
                          />
                          {req.status === 'pending' ? 'Pendiente' : req.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                        </span>
                      </td>
                      <td>
                        {isPending ? (
                          <div className="sa-actions">
                            <button
                              className="sa-btn-toggle activate"
                              title="Aprobar (+5 días)"
                              onClick={() => handleTrialExtAction(req, 'approved')}
                              disabled={trialExtActing === req.id}
                            >
                              {trialExtActing === req.id ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                            </button>
                            <button
                              className="sa-btn-toggle deactivate"
                              title="Rechazar"
                              onClick={() => handleTrialExtAction(req, 'rejected')}
                              disabled={trialExtActing === req.id}
                            >
                              {trialExtActing === req.id ? <span className="sa-spinner sa-spinner-sm" /> : <BlockIcon />}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.78rem', color: '#334155' }}>
                            {req.reviewed_at ? fmtDate(req.reviewed_at) : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Promo codes section ── */}
      <div className="sa-section">
        <div className="sa-section-header">
          <div>
            <h3 className="sa-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <PromoIcon />
              Códigos Promocionales
            </h3>
            <p className="sa-section-sub">
              {promoCodes.length} código{promoCodes.length !== 1 ? 's' : ''} en total
              {promoCodes.filter(p => p.active).length > 0 && ` · ${promoCodes.filter(p => p.active).length} activo${promoCodes.filter(p => p.active).length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button className="sa-btn-primary" onClick={openPromoCreate}>
            <PlusIcon /> Nuevo código
          </button>
        </div>

        <div className="sa-table-wrap">
          {promoLoading ? (
            <div className="sa-loading"><span className="sa-spinner" /><p>Cargando códigos…</p></div>
          ) : promoCodes.length === 0 ? (
            <div className="sa-empty">
              <PromoIcon />
              <p className="sa-empty-title">Sin códigos promocionales</p>
              <p className="sa-empty-sub">Crea el primer código con el botón de arriba.</p>
            </div>
          ) : (
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descuento</th>
                  <th>Descripción</th>
                  <th>Usos</th>
                  <th>Estado</th>
                  <th>Expiración</th>
                  <th style={{ width: 130 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((p) => (
                  <tr key={p.id} className={p.active ? '' : 'sa-row-inactive'}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#e2e8f0', letterSpacing: '0.06em' }}>
                        {p.code}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: '#2FBF71', fontWeight: 700 }}>{p.discount_percentage}%</span>
                    </td>
                    <td style={{ color: '#94a3b8', fontSize: '0.84rem', maxWidth: 200 }}>
                      {p.description ?? <span style={{ color: '#334155' }}>—</span>}
                    </td>
                    <td>
                      <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>
                        {p.uses_count}{p.max_uses != null ? ` / ${p.max_uses}` : ''}
                      </span>
                    </td>
                    <td>
                      <span
                        className="sa-status-badge"
                        style={p.active
                          ? { color: '#22c55e', background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)' }
                          : { color: '#475569', background: 'rgba(71,85,105,0.1)', borderColor: 'rgba(71,85,105,0.3)' }
                        }
                      >
                        <span className="sa-status-dot" style={{ background: p.active ? '#22c55e' : '#475569' }} />
                        {p.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="sa-td-date">
                      {p.expires_at ? fmtDate(p.expires_at) : <span style={{ color: '#334155' }}>Sin límite</span>}
                    </td>
                    <td>
                      <div className="sa-actions">
                        <button
                          className={`sa-btn-toggle ${p.active ? 'deactivate' : 'activate'}`}
                          title={p.active ? 'Desactivar' : 'Activar'}
                          onClick={() => handlePromoToggle(p)}
                          disabled={promoToggling === p.id}
                        >
                          {promoToggling === p.id
                            ? <span className="sa-spinner sa-spinner-sm" />
                            : p.active ? <BlockIcon /> : <CheckIcon />
                          }
                        </button>
                        <button
                          className="sa-btn-icon"
                          title="Editar"
                          onClick={() => openPromoEdit(p)}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="sa-btn-icon sa-btn-icon-red"
                          title="Eliminar"
                          onClick={() => setPromoDeleteTarget(p)}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Log de Invitaciones ── */}
      <div className="sa-section">
        <div
          className="sa-section-header"
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setShowInvitations(v => !v)}
        >
          <div>
            <h3 className="sa-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              📨 Log de Invitaciones
              <span style={{
                background: 'rgba(99,102,241,0.15)',
                color: '#818cf8',
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 999,
              }}>
                {invitations.length}
              </span>
            </h3>
            <p className="sa-section-sub">
              {invitations.filter(i => i.accepted_at).length} aceptadas ·{' '}
              {invitations.filter(i => !i.accepted_at && new Date(i.expires_at) > new Date()).length} pendientes
            </p>
          </div>
          <span style={{ color: '#64748b', fontSize: '1.1rem' }}>{showInvitations ? '▲' : '▼'}</span>
        </div>

        {showInvitations && (
          <div className="sa-table-wrap">
            {invitations.length === 0 ? (
              <div className="sa-empty">
                <p className="sa-empty-title">Sin invitaciones registradas</p>
              </div>
            ) : (
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Email invitado</th>
                    <th>Rol</th>
                    <th>Invitado por</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Expira</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map(inv => {
                    const expired  = !inv.accepted_at && new Date(inv.expires_at) <= new Date();
                    const accepted = !!inv.accepted_at;
                    const badge = accepted
                      ? { label: 'Aceptada', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' }
                      : expired
                      ? { label: 'Expirada', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' }
                      : { label: 'Pendiente', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' };
                    return (
                      <tr key={inv.id}>
                        <td>{inv.company_name}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{inv.email}</td>
                        <td>{inv.role}</td>
                        <td style={{ fontSize: '0.82rem', color: '#94a3b8' }}>{inv.invited_by_email}</td>
                        <td style={{ fontSize: '0.82rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {new Date(inv.created_at).toLocaleDateString('es-EC')}
                        </td>
                        <td>
                          <span style={{
                            background: badge.bg,
                            color: badge.color,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: 999,
                          }}>
                            {badge.label}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                          {new Date(inv.expires_at).toLocaleDateString('es-EC')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Toast ── */}
      {toast && <div className="sa-toast">{toast}</div>}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Nueva empresa
      ══════════════════════════════════════════════════════════════ */}
      {newOpen && (
        <div className="sa-overlay" onClick={(e) => e.target === e.currentTarget && setNewOpen(false)}>
          <div className="sa-modal">
            <div className="sa-modal-header">
              <h4>Nueva empresa</h4>
              <button className="sa-modal-close" onClick={() => setNewOpen(false)}><CloseIcon /></button>
            </div>

            <form className="sa-form" onSubmit={handleNewCompany}>

              <div className="sa-field-row">
                <div className="sa-field">
                  <label>Nombre <span className="sa-required">*</span></label>
                  <input
                    type="text"
                    placeholder="Empresa Química S.A."
                    value={newForm.name}
                    onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                    autoFocus
                    disabled={saving}
                  />
                </div>
                <div className="sa-field">
                  <label>RUC / NIT</label>
                  <input
                    type="text"
                    placeholder="1790012345001"
                    value={newForm.ruc}
                    onChange={(e) => setNewForm((f) => ({ ...f, ruc: e.target.value }))}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="sa-field">
                <label>País</label>
                <select
                  value={newForm.country}
                  onChange={(e) => setNewForm((f) => ({ ...f, country: e.target.value }))}
                  disabled={saving}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="sa-field">
                <label>Plan</label>
                <div className="sa-plan-options">
                  {(['starter', 'professional', 'enterprise'] as const).map((p) => (
                    <label
                      key={p}
                      className={`sa-plan-option ${newForm.plan === p ? 'selected' : ''}`}
                      style={newForm.plan === p ? {
                        borderColor: PLAN_COLORS[p],
                        background:  PLAN_COLORS[p] + '18',
                      } : {}}
                    >
                      <input
                        type="radio"
                        name="new-plan"
                        checked={newForm.plan === p}
                        onChange={() => setNewForm((f) => ({ ...f, plan: p }))}
                      />
                      <span
                        className="sa-plan-name"
                        style={{ color: newForm.plan === p ? PLAN_COLORS[p] : undefined }}
                      >
                        {PLAN_LABELS[p]}
                      </span>
                      <span className="sa-plan-price">
                        ${MONTHLY_PRICES[p]}<span className="sa-plan-period">/mes</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sa-field">
                <label>Tipo de pago</label>
                <div className="sa-cycle-options">
                  <label className={`sa-cycle-option ${newForm.billing_cycle === 'monthly' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="new-cycle"
                      checked={newForm.billing_cycle === 'monthly'}
                      onChange={() => setNewForm((f) => ({ ...f, billing_cycle: 'monthly' }))}
                    />
                    <span>Mensual</span>
                    <span className="sa-cycle-price">${MONTHLY_PRICES[newForm.plan]}/mes</span>
                  </label>
                  <label className={`sa-cycle-option ${newForm.billing_cycle === 'annual' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="new-cycle"
                      checked={newForm.billing_cycle === 'annual'}
                      onChange={() => setNewForm((f) => ({ ...f, billing_cycle: 'annual' }))}
                    />
                    <div className="sa-cycle-label">
                      <span>Anual</span>
                      <span className="sa-cycle-discount">−20%</span>
                    </div>
                    <span className="sa-cycle-price">
                      ${ANNUAL_MONTHLY_PRICES[newForm.plan]}/mes
                    </span>
                  </label>
                </div>
              </div>

              <div className="sa-field">
                <label>Período de prueba gratuita</label>
                <div className="sa-trial-row">
                  <input
                    type="number"
                    className="sa-trial-input"
                    min={0}
                    max={90}
                    value={newForm.trial_days}
                    onChange={(e) => setNewForm((f) => ({ ...f, trial_days: Math.max(0, parseInt(e.target.value) || 0) }))}
                    disabled={saving}
                  />
                  <span className="sa-trial-unit">días</span>
                  <span className="sa-trial-hint">
                    {newForm.trial_days === 0
                      ? 'Sin período de prueba'
                      : `Cobro inicia el ${fmtDate(addDays(new Date(), newForm.trial_days).toISOString())}`
                    }
                  </span>
                </div>
              </div>

              {saveError && <p className="sa-error">{saveError}</p>}

              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-secondary" onClick={() => setNewOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="sa-btn-primary" disabled={saving}>
                  {saving ? <span className="sa-spinner sa-spinner-sm" /> : <PlusIcon />}
                  {saving ? 'Creando…' : 'Crear empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Cambiar plan
      ══════════════════════════════════════════════════════════════ */}
      {editCompany && (
        <div className="sa-overlay" onClick={(e) => e.target === e.currentTarget && setEditCompany(null)}>
          <div className="sa-modal">
            <div className="sa-modal-header">
              <div>
                <h4>Cambiar plan</h4>
                <p className="sa-modal-sub">{editCompany.name}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setEditCompany(null)}><CloseIcon /></button>
            </div>

            <form className="sa-form" onSubmit={handleEditPlan}>

              <div className="sa-field">
                <label>Plan</label>
                <div className="sa-plan-options">
                  {(['starter', 'professional', 'enterprise'] as const).map((p) => (
                    <label
                      key={p}
                      className={`sa-plan-option ${editPlan === p ? 'selected' : ''}`}
                      style={editPlan === p ? {
                        borderColor: PLAN_COLORS[p],
                        background:  PLAN_COLORS[p] + '18',
                      } : {}}
                    >
                      <input
                        type="radio"
                        name="edit-plan"
                        checked={editPlan === p}
                        onChange={() => setEditPlan(p)}
                      />
                      <span
                        className="sa-plan-name"
                        style={{ color: editPlan === p ? PLAN_COLORS[p] : undefined }}
                      >
                        {PLAN_LABELS[p]}
                      </span>
                      <span className="sa-plan-price">
                        ${MONTHLY_PRICES[p]}<span className="sa-plan-period">/mes</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sa-field">
                <label>Tipo de pago</label>
                <div className="sa-cycle-options">
                  <label className={`sa-cycle-option ${editCycle === 'monthly' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="edit-cycle"
                      checked={editCycle === 'monthly'}
                      onChange={() => setEditCycle('monthly')}
                    />
                    <span>Mensual</span>
                    <span className="sa-cycle-price">${MONTHLY_PRICES[editPlan]}/mes</span>
                  </label>
                  <label className={`sa-cycle-option ${editCycle === 'annual' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="edit-cycle"
                      checked={editCycle === 'annual'}
                      onChange={() => setEditCycle('annual')}
                    />
                    <div className="sa-cycle-label">
                      <span>Anual</span>
                      <span className="sa-cycle-discount">−20%</span>
                    </div>
                    <span className="sa-cycle-price">
                      ${ANNUAL_MONTHLY_PRICES[editPlan]}/mes
                    </span>
                  </label>
                </div>
              </div>

              <div className="sa-field">
                <label>Fecha próximo cobro</label>
                <input
                  type="date"
                  value={editNextBilling}
                  onChange={(e) => setEditNextBilling(e.target.value)}
                  disabled={editSaving}
                />
              </div>

              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-secondary" onClick={() => setEditCompany(null)}>
                  Cancelar
                </button>
                <button type="submit" className="sa-btn-primary" disabled={editSaving}>
                  {editSaving ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                  {editSaving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Registrar pago
      ══════════════════════════════════════════════════════════════ */}
      {paymentCompany && (
        <div className="sa-overlay" onClick={(e) => e.target === e.currentTarget && setPaymentCompany(null)}>
          <div className="sa-modal">
            <div className="sa-modal-header">
              <div>
                <h4>Registrar pago</h4>
                <p className="sa-modal-sub">{paymentCompany.name}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setPaymentCompany(null)}><CloseIcon /></button>
            </div>

            <form className="sa-form" onSubmit={handleRegisterPayment}>

              <div className="sa-pay-info-box">
                <span>Plan actual:</span>
                <strong style={{ color: PLAN_COLORS[paymentCompany.plan] ?? '#f1f5f9' }}>
                  {PLAN_LABELS[paymentCompany.plan] || paymentCompany.plan}
                </strong>
                <span>·</span>
                <span>${planPrice(paymentCompany.plan, paymentCompany.billing_cycle)}/mes ({paymentCompany.billing_cycle === 'annual' ? 'anual' : 'mensual'})</span>
              </div>

              <div className="sa-field-row">
                <div className="sa-field">
                  <label>Monto recibido <span className="sa-required">*</span></label>
                  <input
                    type="number"
                    placeholder={String(planPrice(paymentCompany.plan, paymentCompany.billing_cycle))}
                    min="0"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, amount: e.target.value }))}
                    autoFocus
                    disabled={paymentSaving}
                  />
                </div>
                <div className="sa-field">
                  <label>Fecha de pago <span className="sa-required">*</span></label>
                  <input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, payment_date: e.target.value }))}
                    disabled={paymentSaving}
                  />
                </div>
              </div>

              <div className="sa-field">
                <label>Método de pago</label>
                <div className="sa-cycle-options" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                  {(['transferencia', 'efectivo', 'otro'] as const).map((m) => (
                    <label
                      key={m}
                      className={`sa-cycle-option ${paymentForm.method === m ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="pay-method"
                        checked={paymentForm.method === m}
                        onChange={() => setPaymentForm((f) => ({ ...f, method: m }))}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{m}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sa-field">
                <label>Referencia / Comprobante</label>
                <input
                  type="text"
                  placeholder="Nro. de transferencia, recibo, etc."
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, reference: e.target.value }))}
                  disabled={paymentSaving}
                />
              </div>

              <div className="sa-field">
                <label>Notas internas</label>
                <textarea
                  className="sa-textarea"
                  placeholder="Observaciones del pago…"
                  rows={2}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))}
                  disabled={paymentSaving}
                />
              </div>

              {paymentForm.payment_date && (
                <div className="sa-pay-next-hint">
                  <CheckIcon />
                  Al confirmar: estado → <strong>Activo</strong> · próximo cobro{' '}
                  <strong>{fmtDate(sumarDiasDia(paymentForm.payment_date, 30))}</strong>
                </div>
              )}

              {paymentError && <p className="sa-error">{paymentError}</p>}

              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-secondary" onClick={() => setPaymentCompany(null)}>
                  Cancelar
                </button>
                <button type="submit" className="sa-btn-primary" disabled={paymentSaving}>
                  {paymentSaving ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                  {paymentSaving ? 'Registrando…' : 'Confirmar pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Contactar administrador
      ══════════════════════════════════════════════════════════════ */}
      {contactCompany && (
        <div className="sa-overlay" onClick={(e) => e.target === e.currentTarget && setContactCompany(null)}>
          <div className="sa-modal sa-modal-narrow">
            <div className="sa-modal-header">
              <div>
                <h4>Contactar administrador</h4>
                <p className="sa-modal-sub">{contactCompany.name}{contactCompany.ruc ? ` · RUC ${contactCompany.ruc}` : ''}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setContactCompany(null)}><CloseIcon /></button>
            </div>

            <div className="sa-form">
              {contactLoading ? (
                <div className="sa-loading sa-loading-modal"><span className="sa-spinner" /><p>Cargando contacto…</p></div>
              ) : !contactInfo ? (
                <div className="sa-empty sa-empty-modal">
                  <ContactIcon />
                  <p className="sa-empty-title">Sin administrador registrado</p>
                  <p className="sa-empty-sub">Esta empresa no tiene un perfil con rol "admin".</p>
                </div>
              ) : (
                <div className="sa-contact-info">
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Empresa</span>
                    <span className="sa-contact-value">{contactCompany.name}</span>
                  </div>
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Dirección</span>
                    <span className="sa-contact-value">{contactCompany.direccion ?? '—'}</span>
                  </div>
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Administrador</span>
                    <span className="sa-contact-value">{contactInfo.full_name ?? '—'}</span>
                  </div>
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Cargo</span>
                    <span className="sa-contact-value">{contactInfo.job_title ?? '—'}</span>
                  </div>
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Correo</span>
                    <div className="sa-contact-value-with-action">
                      <span className="sa-contact-value">{contactInfo.email ?? '—'}</span>
                      {contactInfo.email && (
                        <button type="button" className="sa-btn-secondary sa-contact-copy" onClick={handleCopyEmail}>
                          {contactCopied ? 'Copiado ✓' : 'Copiar correo'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Teléfono</span>
                    <div className="sa-contact-value-with-action">
                      <span className="sa-contact-value">{contactInfo.phone ?? '—'}</span>
                      {contactInfo.phone && (
                        <button type="button" className="sa-btn-secondary sa-contact-copy" onClick={handleCopyPhone}>
                          {contactPhoneCopied ? 'Copiado ✓' : 'Copiar teléfono'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="sa-contact-row">
                    <span className="sa-contact-label">Registro</span>
                    <span className="sa-contact-value">{fmtDate(contactCompany.created_at)}</span>
                  </div>
                  {contactCompany.trial_ends_at && (daysUntil(contactCompany.trial_ends_at) ?? 0) < 0 && (
                    <div className="sa-contact-row">
                      <span className="sa-contact-label">Trial vencido desde</span>
                      <span className="sa-contact-value" style={{ color: '#f87171' }}>
                        {fmtDate(contactCompany.trial_ends_at)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-secondary" onClick={() => setContactCompany(null)}>
                  Cerrar
                </button>
                <button type="button" className="sa-btn-primary" onClick={contactToPayment}>
                  <DollarSmIcon /> Registrar pago
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Historial de pagos
      ══════════════════════════════════════════════════════════════ */}
      {historyCompany && (
        <div className="sa-overlay" onClick={(e) => e.target === e.currentTarget && setHistoryCompany(null)}>
          <div className="sa-modal sa-modal-wide">
            <div className="sa-modal-header">
              <div>
                <h4>Historial de pagos</h4>
                <p className="sa-modal-sub">{historyCompany.name}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setHistoryCompany(null)}><CloseIcon /></button>
            </div>

            <div style={{ padding: '16px 24px 24px' }}>
              {historyLoading ? (
                <div className="sa-loading sa-loading-modal"><span className="sa-spinner" /><p>Cargando pagos…</p></div>
              ) : paymentsHistory.length === 0 ? (
                <div className="sa-empty sa-empty-modal">
                  <DollarIcon />
                  <p className="sa-empty-title">Sin pagos registrados</p>
                  <p className="sa-empty-sub">Usa el botón $ en la tabla para registrar el primer pago.</p>
                </div>
              ) : (
                <div className="sa-table-wrap">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Monto</th>
                        <th>Método</th>
                        <th>Referencia</th>
                        <th>Notas</th>
                        <th>Registrado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsHistory.map((p) => (
                        <tr key={p.id}>
                          <td className="sa-td-date">{fmtDate(p.payment_date)}</td>
                          <td>
                            <span style={{ color: '#22c55e', fontWeight: 700 }}>
                              ${Number(p.amount).toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td>
                            <span className="sa-pay-method-badge">{p.method}</span>
                          </td>
                          <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                            {p.reference ?? <span style={{ color: '#334155' }}>—</span>}
                          </td>
                          <td style={{ color: '#64748b', fontSize: '0.78rem', maxWidth: 160 }}>
                            {p.notes ?? '—'}
                          </td>
                          <td className="sa-td-date">{fmtDate(p.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Eliminar empresa — paso 1
      ══════════════════════════════════════════════════════════════ */}
      {deleteCompany && deleteStep === 1 && (
        <div className="sa-overlay">
          <div className="sa-modal sa-modal-narrow">
            <div className="sa-modal-header">
              <div>
                <h4 style={{ color: '#f87171' }}>Eliminar empresa</h4>
                <p className="sa-modal-sub">{deleteCompany.name}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setDeleteCompany(null)}><CloseIcon /></button>
            </div>

            <div className="sa-form">
              <div className="sa-delete-warning">
                <AlertIcon />
                <div>
                  <p style={{ color: '#fca5a5', fontWeight: 700, margin: '0 0 6px' }}>
                    Esta acción es permanente e irreversible.
                  </p>
                  <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: 0 }}>
                    Se eliminarán todos los datos asociados: productos, bodegas, usuarios,
                    invitaciones, registros de auditoría, pagos y solicitudes.
                  </p>
                </div>
              </div>

              <div className="sa-modal-footer">
                <button className="sa-btn-secondary" onClick={() => setDeleteCompany(null)}>
                  Cancelar
                </button>
                <button
                  className="sa-btn-danger"
                  onClick={() => setDeleteStep(2)}
                >
                  <TrashIcon /> Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal eliminar: paso 2 — confirmar nombre */}
      {deleteCompany && deleteStep === 2 && (
        <div className="sa-overlay">
          <div className="sa-modal sa-modal-narrow">
            <div className="sa-modal-header">
              <div>
                <h4 style={{ color: '#f87171' }}>Confirmar eliminación</h4>
                <p className="sa-modal-sub">{deleteCompany.name}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setDeleteCompany(null)}><CloseIcon /></button>
            </div>

            <div className="sa-form">
              <div className="sa-field">
                <label>
                  Escribe el nombre de la empresa para confirmar:
                  <span className="sa-delete-company-name"> {deleteCompany.name}</span>
                </label>
                <input
                  type="text"
                  placeholder={deleteCompany.name}
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  autoFocus
                  disabled={deleteSaving}
                />
                <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '6px 0 0' }}>
                  Escribe el nombre exacto (sin distinguir mayúsculas)
                </p>
              </div>

              <div className="sa-modal-footer">
                <button className="sa-btn-secondary" onClick={() => setDeleteCompany(null)}>
                  Cancelar
                </button>
                <button
                  className="sa-btn-danger"
                  onClick={handleDeleteCompany}
                  disabled={deleteSaving || deleteConfirmName.trim().toLowerCase() !== deleteCompany.name.trim().toLowerCase()}
                >
                  {deleteSaving ? <span className="sa-spinner sa-spinner-sm" /> : <TrashIcon />}
                  {deleteSaving ? 'Eliminando…' : 'Eliminar permanentemente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Crear / editar código promocional
      ══════════════════════════════════════════════════════════════ */}
      {promoModalOpen && (
        <div className="sa-overlay" onClick={(e) => e.target === e.currentTarget && setPromoModalOpen(false)}>
          <div className="sa-modal">
            <div className="sa-modal-header">
              <h4>{promoEditTarget ? 'Editar código' : 'Nuevo código promocional'}</h4>
              <button className="sa-modal-close" onClick={() => setPromoModalOpen(false)}><CloseIcon /></button>
            </div>
            <form className="sa-form" onSubmit={handlePromoSave}>
              <div className="sa-field-row">
                <div className="sa-field">
                  <label>Código <span className="sa-required">*</span></label>
                  <input
                    type="text"
                    placeholder="BIENVENIDO20"
                    value={promoForm.code}
                    onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                    autoFocus
                    disabled={promoSaving}
                    style={{ fontFamily: 'monospace', letterSpacing: '0.06em', textTransform: 'uppercase' }}
                    maxLength={32}
                  />
                </div>
                <div className="sa-field">
                  <label>Descuento % <span className="sa-required">*</span></label>
                  <input
                    type="number"
                    placeholder="20"
                    min={1}
                    max={100}
                    value={promoForm.discount_percentage}
                    onChange={(e) => setPromoForm((f) => ({ ...f, discount_percentage: e.target.value }))}
                    disabled={promoSaving}
                  />
                </div>
              </div>
              <div className="sa-field">
                <label>Descripción</label>
                <input
                  type="text"
                  placeholder="Campaña de lanzamiento Q1 2026"
                  value={promoForm.description}
                  onChange={(e) => setPromoForm((f) => ({ ...f, description: e.target.value }))}
                  disabled={promoSaving}
                />
              </div>
              <div className="sa-field-row">
                <div className="sa-field">
                  <label>Máximo de usos <span style={{ color: '#64748b', fontSize: '0.75rem' }}>(opcional)</span></label>
                  <input
                    type="number"
                    placeholder="Sin límite"
                    min={1}
                    value={promoForm.max_uses}
                    onChange={(e) => setPromoForm((f) => ({ ...f, max_uses: e.target.value }))}
                    disabled={promoSaving}
                  />
                </div>
                <div className="sa-field">
                  <label>Fecha expiración <span style={{ color: '#64748b', fontSize: '0.75rem' }}>(opcional)</span></label>
                  <input
                    type="date"
                    value={promoForm.expires_at}
                    onChange={(e) => setPromoForm((f) => ({ ...f, expires_at: e.target.value }))}
                    disabled={promoSaving}
                  />
                </div>
              </div>
              {promoFormError && <p className="sa-error">{promoFormError}</p>}
              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-secondary" onClick={() => setPromoModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="sa-btn-primary" disabled={promoSaving}>
                  {promoSaving ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                  {promoSaving ? 'Guardando…' : promoEditTarget ? 'Guardar cambios' : 'Crear código'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Confirmar eliminación código ── */}
      {promoDeleteTarget && (
        <div className="sa-overlay">
          <div className="sa-modal sa-modal-narrow">
            <div className="sa-modal-header">
              <div>
                <h4 style={{ color: '#f87171' }}>Eliminar código</h4>
                <p className="sa-modal-sub">{promoDeleteTarget.code}</p>
              </div>
              <button className="sa-modal-close" onClick={() => setPromoDeleteTarget(null)}><CloseIcon /></button>
            </div>
            <div className="sa-form">
              <div className="sa-delete-warning">
                <AlertIcon />
                <div>
                  <p style={{ color: '#fca5a5', fontWeight: 700, margin: '0 0 6px' }}>
                    Esta acción es permanente.
                  </p>
                  <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: 0 }}>
                    El código <strong style={{ color: '#e2e8f0' }}>{promoDeleteTarget.code}</strong> ({promoDeleteTarget.discount_percentage}% desc.)
                    se eliminará. Los usuarios que ya lo aplicaron no se verán afectados.
                  </p>
                </div>
              </div>
              <div className="sa-modal-footer">
                <button className="sa-btn-secondary" onClick={() => setPromoDeleteTarget(null)}>
                  Cancelar
                </button>
                <button className="sa-btn-danger" onClick={handlePromoDelete} disabled={promoDeleting}>
                  {promoDeleting ? <span className="sa-spinner sa-spinner-sm" /> : <TrashIcon />}
                  {promoDeleting ? 'Eliminando…' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Modal: Cambiar estado Layout Pro
      ══════════════════════════════════════════════════════════════ */}
      {layoutModal && (
        <div className="sa-overlay" onClick={e => e.target === e.currentTarget && setLayoutModal(null)}>
          <div className="sa-modal">
            <div className="sa-modal-header">
              <div>
                <h4>Actualizar solicitud</h4>
                <p className="sa-modal-sub">
                  {layoutModal.companies?.name ?? '—'} · {layoutModal.warehouse_name ?? 'Sin bodega'}
                </p>
              </div>
              <button className="sa-modal-close" onClick={() => setLayoutModal(null)}><CloseIcon /></button>
            </div>

            <form className="sa-form" onSubmit={handleLayoutStatus}>

              {(() => {
                const dims = layoutModal.dimensions as any;
                return (
                  <div className="sa-layout-summary">
                    {dims.largo && <span>{dims.largo} × {dims.ancho} × {dims.altura} m</span>}
                    {dims.zonas?.length > 0 && <span>{dims.zonas.length} zona{dims.zonas.length !== 1 ? 's' : ''}</span>}
                    {dims.orientacion && <span>Orientación {dims.orientacion}</span>}
                    {dims.contra_incendios && <span>{dims.contra_incendios}</span>}
                    {layoutModal.file_url && (
                      <EnlaceArchivo bucket="layout-files" valor={layoutModal.file_url} className="sa-layout-file-link">
                        Ver plano del cliente
                      </EnlaceArchivo>
                    )}
                    {layoutModal.notes && (
                      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '6px 0 0', gridColumn: '1/-1' }}>
                        {layoutModal.notes}
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="sa-field">
                <label>Estado</label>
                <div className="sa-cycle-options">
                  {(['pendiente', 'en_proceso', 'entregado'] as const).map(s => {
                    const cfg = STATUS_CFG[s];
                    return (
                      <label key={s}
                        className={`sa-cycle-option ${layoutNewStatus === s ? 'selected' : ''}`}
                        style={layoutNewStatus === s ? { borderColor: cfg.color, background: cfg.bg } : {}}
                      >
                        <input type="radio" name="layout-status" checked={layoutNewStatus === s}
                          onChange={() => setLayoutNewStatus(s)} />
                        <span style={{ color: layoutNewStatus === s ? cfg.color : undefined, fontWeight: 600 }}>
                          {cfg.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="sa-field">
                <label>Subir layout entregado (PDF)</label>
                <div
                  className="sa-layout-upload-area"
                  onClick={() => layoutFileRef.current?.click()}
                >
                  {layoutFile ? (
                    <span style={{ color: '#a78bfa', fontWeight: 600 }}>{layoutFile.name}</span>
                  ) : (
                    <span>Haz clic para adjuntar el PDF del layout · Máx. 20 MB</span>
                  )}
                </div>
                <input
                  ref={layoutFileRef}
                  type="file"
                  accept=".pdf,image/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const sizeError = validateFileSize(f, 20);
                    if (sizeError) { showToast(sizeError); e.target.value = ''; return; }
                    setLayoutFile(f);
                  }}
                />
                {layoutModal.layout_url && (
                  <p style={{ fontSize: '0.75rem', color: '#475569', margin: '4px 0 0' }}>
                    Ya tiene un layout entregado.{' '}
                    <EnlaceArchivo bucket="layout-files" valor={layoutModal.layout_url}
                      style={{ color: '#60a5fa' }}>
                      Ver actual
                    </EnlaceArchivo>
                  </p>
                )}
              </div>

              <div className="sa-modal-footer">
                <button type="button" className="sa-btn-secondary" onClick={() => setLayoutModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="sa-btn-primary" disabled={layoutSaving}>
                  {layoutSaving ? <span className="sa-spinner sa-spinner-sm" /> : <CheckIcon />}
                  {layoutSaving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({
  label, value, icon, color, bg, sub,
}: {
  label: string;
  value: number | string;
  icon:  React.ReactNode;
  color: string;
  bg:    string;
  sub?:  string;
}) {
  return (
    <div className="sa-metric-card">
      <div className="sa-metric-icon" style={{ color, background: bg }}>{icon}</div>
      <div className="sa-metric-body">
        <div className="sa-metric-value-row">
          <span className="sa-metric-value" style={{ color }}>{value}</span>
          {sub && <span className="sa-metric-sub">{sub}</span>}
        </div>
        <span className="sa-metric-label">{label}</span>
      </div>
    </div>
  );
}

function PlanCell({ plan, cycle }: { plan: string; cycle: string }) {
  const color = PLAN_COLORS[plan] ?? '#64748b';
  return (
    <div className="sa-plan-cell">
      <span
        className="sa-plan-badge"
        style={{ color, borderColor: color + '50', background: color + '12' }}
      >
        {PLAN_LABELS[plan] || plan}
      </span>
      <span className="sa-cycle-tag">
        {cycle === 'annual' ? 'Anual' : 'Mensual'}
      </span>
    </div>
  );
}

function StatusCell({ status, trialEndsAt }: { status: CompanyStatus; trialEndsAt: string | null }) {
  const cfg = {
    trial:    { label: 'Trial',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
    active:   { label: 'Activo',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)'   },
    overdue:  { label: 'Vencido',  color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
    inactive: { label: 'Inactivo', color: '#475569', bg: 'rgba(71,85,105,0.12)'   },
  }[status];

  return (
    <div className="sa-status-cell">
      <span
        className="sa-status-badge"
        style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.color + '40' }}
      >
        <span className="sa-status-dot" style={{ background: cfg.color }} />
        {cfg.label}
      </span>
      {status === 'trial' && trialEndsAt && (
        <span className="sa-trial-expiry">
          hasta {new Date(trialEndsAt).toLocaleDateString('es-CL', { month: 'short', day: 'numeric' })}
        </span>
      )}
    </div>
  );
}

function PaymentStatusCell({
  paymentStatus, lastPaymentDate, nextPaymentDate,
}: {
  paymentStatus:   CompanyWithStats['payment_status'];
  lastPaymentDate: string | null;
  nextPaymentDate: string | null;
}) {
  const ps = paymentStatus ?? 'trial';
  const cfg = {
    trial:     { label: 'Trial',      color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
    active:    { label: 'Al día',     color: '#22c55e', bg: 'rgba(34,197,94,0.1)'   },
    overdue:   { label: 'Moroso',     color: '#ef4444', bg: 'rgba(239,68,68,0.1)'   },
    cancelled: { label: 'Cancelado',  color: '#475569', bg: 'rgba(71,85,105,0.1)'   },
  }[ps] ?? { label: ps, color: '#64748b', bg: 'rgba(100,116,139,0.1)' };

  return (
    <div className="sa-status-cell">
      <span
        className="sa-status-badge"
        style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.color + '40' }}
      >
        <span className="sa-status-dot" style={{ background: cfg.color }} />
        {cfg.label}
      </span>
      {lastPaymentDate && (
        <span className="sa-trial-expiry">
          últ. {aFechaLocal(lastPaymentDate).toLocaleDateString('es-CL', { month: 'short', day: 'numeric' })}
        </span>
      )}
      {nextPaymentDate && ps === 'active' && (
        <span className="sa-trial-expiry">
          próx. {aFechaLocal(nextPaymentDate).toLocaleDateString('es-CL', { month: 'short', day: 'numeric' })}
        </span>
      )}
    </div>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────────

function BuildingIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M8 21v-4a4 4 0 018 0v4" />
      <rect x="8" y="10" width="2" height="2" /><rect x="14" y="10" width="2" height="2" />
      <rect x="8" y="15" width="2" height="2" /><rect x="14" y="15" width="2" height="2" />
    </svg>
  );
}
function DollarIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  );
}
function DollarSmIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function BlockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}
function ListIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}
function UsersSmIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}
function BoxSmIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  );
}
function WhSmIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  );
}
function PromoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}
function ContactIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <circle cx="9" cy="10" r="2.5" />
      <path d="M5 17c0-2.2 1.8-3.5 4-3.5s4 1.3 4 3.5" />
      <line x1="15" y1="8" x2="19" y2="8" /><line x1="15" y1="12" x2="19" y2="12" />
    </svg>
  );
}
